import { action, computed, IReactionDisposer, observable, reaction, toJS } from "mobx";
import debug from "debug";
import { ObservableSubscription } from "@apollo/client";
import { debounce } from "lodash";
import { SimpleEventEmitter } from "@utils/SimpleEventEmitter";
import { VirtualSelectorProps, SearchItem } from "./utils";

const log = debug("app:VirtualSelector:VirtualSelectorStore");

export class VirtualSelectorStore<
  FILTER extends {},
  SEARCH_ITEM extends SearchItem,
  VALUE
> {
  onFetchEvent = new SimpleEventEmitter();
  @observable public selected: SEARCH_ITEM[] = [];
  @observable public showSelected = false;

  @observable private _isSearchOpen = false;
  @observable private _filter: FILTER = this.props.initialFilter;
  @observable private _searchResult: SEARCH_ITEM[] = [];
  @observable private _isFetchingMore = false;
  @observable private _isFetching = true;
  @observable private _hasMore = true;

  private subscription: ObservableSubscription;
  private disposer: IReactionDisposer;

  constructor(public props: VirtualSelectorProps<FILTER, SEARCH_ITEM, VALUE>) {
    (window as any).virtualSelectorStore = this;
  }

  init() {
    this.initReactions();
  }

  initReactions() {
    const debouncedFetch = debounce(() => this.fetch(), 500);

    this.disposer = reaction(() => this._filter, debouncedFetch, {
      fireImmediately: true,
      name: "react to filter"
    });
  }

  unsubscribe() {
    this.disposer && this.disposer();
    this.subscription && this.subscription.unsubscribe();
  }

  @computed get selectedCustomerItem() {
    const selectedIds = this.selected.map((x) => x._id);
    return this._searchResult.filter((x) => selectedIds.includes(x._id));
  }

  @computed get isSearchOpen() {
    return this._isSearchOpen;
  }

  @computed get isLoading() {
    return this._isFetching; // || this._isFetchingMore;
  }

  @computed get isFetching() {
    return this._isFetching;
  }

  @computed get filter() {
    return this._filter;
  }

  @computed get searchResult() {
    return this._searchResult;
  }

  @computed get isWaiting() {
    return this.props.shouldFetch && !this.props.shouldFetch(this._filter);
  }

  @action.bound openSearchDialog(initialFilter?: FILTER) {
    if (initialFilter) {
      this._filter = initialFilter;
    }

    this.init();
    this.selected = [];
    this._isSearchOpen = true;
  }

  @action.bound addMore(initialFilter?: FILTER, selected?: SEARCH_ITEM[]) {
    if (initialFilter) {
      this._filter = initialFilter;
    }

    this.init();
    if (selected) {
      this.selected = selected;
      this.selectMultiple(false);
    }
    this._isSearchOpen = true;
  }

  @action handleShowSelected = () => {
    this.showSelected = !this.showSelected;
  };

  @action.bound selectMultiple(close = true) {
    if (this.props.multi === true) {
      const values = toJS(this.selected);
      this.props.onChange && this.props.onChange(values);
    }
    if (close) {
      this.closeSearchDialog();
    }
  }

  @action.bound closeSearchDialog() {
    this.unsubscribe();
    this._searchResult = [];
    this._isSearchOpen = false;
    this.props.onSearchClose?.();
  }

  @action.bound setResult(items: SEARCH_ITEM[]) {
    this._searchResult = items;
    this._isFetching = false;
    this._hasMore = items.length > 0;
  }

  @action.bound fetch() {
    log("fetch");

    if (this.props.shouldFetch && !this.props.shouldFetch(this._filter)) {
      log("shouldFetch is false");
      return;
    }

    this._hasMore = true;
    this._isFetching = true;

    this.props
      .fetch()
      .then((res) => {
        log("fetch result", res);

        this._searchResult = res;
        this._hasMore = res.length > 0;
      })
      .finally(() => {
        this._isFetching = false;
        this.onFetchEvent.trigger();
      });
  }

  @action.bound fetchMore(skip: number, limit: number) {
    log("fetchMore");

    if (this.props.shouldFetch && !this.props.shouldFetch(this._filter)) {
      log("shouldFetch is false");
      return;
    }

    this._isFetchingMore = true;

    return this.props
      .fetchMore({ skip, limit })
      .then((items) => {
        return this._searchResult.push(...items);
      })
      .finally(() => {
        this._isFetchingMore = false;
      });
  }

  @action.bound selectItem(item: SEARCH_ITEM) {
    log("selectItem", item);
    if (this.props.multi) {
      const existing = this.selected.find((s) => s._id === item._id);
      if (existing) {
        this.selected = this.selected.filter((s) => s !== existing);
      } else {
        this.selected.push(item);
      }
    } else if (this.props.multi === false) {
      this.props.onChange && this.props.onChange(toJS(item));
      this.closeSearchDialog();
    }
  }

  @action.bound clear() {
    log("clear");
    if (this.props.multi === true) {
      this.selected = [];

      this.props.onChange([]);
    } else if (this.props.multi === false) {
      this.props.onChange(null);
    }
    this.closeSearchDialog();
  }

  @action.bound setFilter(v: Partial<FILTER>) {
    this._filter = {
      ...this.filter,
      ...v
    };
  }
}
